/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */
'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useLanguage } from '@/providers/LanguageProvider';
import { request } from '@/services/request';
import { toast } from 'sonner';
import { AssociableApps, Component } from 'types/api-data';

import { createNameWithSuffix } from '@/lib/helper';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Icons } from '@/components/icons';
import BasicLoader from '@/components/spinner/basic-loader';
import { DetailsPageSpinner } from '@/components/spinner/details-page-spinner';

import EllipsesDropdown from './ellipses-dropdown';
import { useWorkflowDependency } from './useWorkflowDependency';

const WorkflowApplication = ({
  component,
  workflowName,
}: {
  component: Component;
  workflowName: string;
}) => {
  // keep track of the search text
  const [query, setQuery] = useState('');

  // loading state for the quick‑create call
  const [isCreating, setIsCreating] = useState(false);

  // loading state for the removing app
  const [isRemoving, setIsRemoving] = useState(false);

  const { messages } = useLanguage();
  const generalIntl = messages?.General;
  const compIntl = messages?.ComponentsPage;

  const {
    selectedApp,
    showList,
    isLoading,
    isUpdating,
    associableApps,
    commandRef,
    setShowList,
    handleUpdate,
    handleRemove,
  } = useWorkflowDependency({
    component,
    workflowName,
    type: 'application',
  });

  const normalizedQuery = query.trim().toLowerCase();

  const filteredApps =
    associableApps?.filter((app: AssociableApps) =>
      app.display_name.toLowerCase().includes(normalizedQuery)
    ) ?? [];

  const hasExactMatch = associableApps?.some(
    (app: AssociableApps) => app.display_name.toLowerCase() === normalizedQuery
  );

  // What to do when "+{query}” is clicked
  const handleCreateNewApp = async () => {
    const nameRaw = query.trim();
    if (!nameRaw) return;

    setShowList(false);
    setIsCreating(true);

    // slugify: lowercase, hyphens instead of spaces
    const appSlug = createNameWithSuffix(nameRaw);

    // payload with default fields
    const payload = {
      display_name: nameRaw.charAt(0).toUpperCase() + nameRaw.slice(1),
      owner: '', // empty
      description: '', // empty
      sla: '3 seconds', // hard‑coded SLA
      components: [], // empty array
      categories: ['New'], // default category
    };

    try {
      const [newApp] = await request.post(
        { endpoint: `apps/${appSlug}` },
        payload
      );
      toast.success(
        messages?.ApplicationForm?.application_created_successfully
      );
      handleUpdate(newApp);
      setQuery('');
      setShowList(false);
    } catch (err: any) {
      if (err.status === 409) {
        toast.error(messages?.ApplicationForm?.application_already_exists);
      } else {
        toast.error(messages?.ApplicationForm?.failed_to_create_app);
        console.error(err);
      }
    } finally {
      setIsCreating(false);
    }
  };

  // Show a disassociating loader on click of remove button
  const handleRemoveWithLoader = async () => {
    setIsRemoving(true);
    try {
      await handleRemove();
    } catch (err) {
      toast.error(messages?.ApplicationForm?.error_removing_application);
      console.error(err);
    } finally {
      setIsRemoving(false);
    }
  };

  return (
    <div className="min-w-[198px] border border-y-0 border-[#40565A] px-4">
      {/* If an app is already associated */}
      {selectedApp && !isCreating && !isUpdating ? (
        <div className="group flex items-center gap-2.5">
          <div>
            <h3 className="pb-1 text-body-sm text-form-input-disabled">
              {generalIntl?.applications}
            </h3>
            {isRemoving || isUpdating ? (
              <div className="flex items-center gap-2">
                {isRemoving
                  ? messages?.General?.disassociating
                  : messages?.General?.associating}
                <BasicLoader className="size-4" />
              </div>
            ) : (
              <Link href={`/apps/${selectedApp.name}`}>
                <p className="text-h4 text-body-primary hover:underline hover:underline-offset-2">
                  {selectedApp.display_name}
                </p>
              </Link>
            )}
          </div>
          <EllipsesDropdown
            onEdit={() => setShowList(true)}
            onRemove={handleRemoveWithLoader}
          />
        </div>
      ) : (
        <>
          {/* If no app is selected */}
          <h3 className="text-body-sm text-form-input-disabled">
            {generalIntl?.applications}
          </h3>
          {isUpdating || isCreating || isRemoving ? (
            <div className="flex items-center gap-2">
              {isCreating
                ? messages?.General?.creating
                : isRemoving
                  ? messages?.General?.disassociating
                  : messages?.General?.associating}
              <BasicLoader className="size-4" />
            </div>
          ) : (
            <div
              className="inline-flex cursor-pointer items-center gap-1 text-link-active"
              onClick={() => setShowList(true)}
            >
              {Icons.Plus('stroke-0')}
              <span className="text-sm">{compIntl?.add_application}</span>
            </div>
          )}
        </>
      )}

      {/* When dropdown is open */}
      {showList && (
        <div className="absolute z-10 mt-2" ref={commandRef}>
          <Command
            shouldFilter={false}
            className="min-w-[240px] rounded-sm rounded-b-none bg-white text-[#464642]"
          >
            <CommandInput
              placeholder={messages?.ComponentsPage?.search_application_message}
              className="text-[#323130] placeholder:text-[#8C9EA1]"
              iconColor="-ml-1"
              value={query}
              onValueChange={setQuery}
            />
            {isLoading ? (
              <div className="p-4 text-center">
                <DetailsPageSpinner />
              </div>
            ) : (
              <CommandList className="max-h-40 overflow-y-auto">
                <CommandEmpty>No results found.</CommandEmpty>

                {/* Render your filtered results */}
                <CommandGroup className="text-black">
                  {filteredApps.map((app: AssociableApps) => (
                    <CommandItem
                      key={app.name}
                      className="cursor-pointer data-[selected=true]:bg-[#EDEBE9] data-[selected=true]:text-[#323130]"
                      onSelect={() => handleUpdate(app)}
                    >
                      {app.display_name}
                    </CommandItem>
                  ))}
                </CommandGroup>

                {/* If the chars are more than 3 then show item as dropdown to create an app. */}

                {query.trim().length > 2 && !hasExactMatch && (
                  <>
                    {/* Only show when no app partially matches the query */}
                    {filteredApps.length === 0 && (
                      <div className="border-b border-[#EBEFF1]  py-[7px] pl-3 text-data-table-cell text-form-input-disabled">
                        No results found
                      </div>
                    )}

                    <CommandGroup
                      heading="Create New Application"
                      className="text-black"
                    >
                      <CommandItem
                        className="cursor-pointer data-[selected=true]:bg-[#EDEBE9] data-[selected=true]:text-[#323130] "
                        onSelect={handleCreateNewApp}
                        key="create-new"
                        value={query}
                        disabled={isCreating}
                      >
                        <span className="flex gap-2">
                          {Icons.Plus('text-icon-fill')}
                          {query?.charAt(0).toUpperCase() + query.slice(1)}
                        </span>
                      </CommandItem>
                    </CommandGroup>
                  </>
                )}
              </CommandList>
            )}
          </Command>
        </div>
      )}
    </div>
  );
};

export default WorkflowApplication;
